import { faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

type ShareDropdownProps = {
  facebookUrl: string;
  twitterUrl: string;
  className?: string;
  menuClassName?: string;
};

const ShareDropdown = ({
  facebookUrl,
  twitterUrl,
  className = "",
  menuClassName = "",
}: ShareDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={menuRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label="Share article"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex h-10 w-10 items-center justify-center transition-colors duration-150 ${
          isOpen
            ? "bg-signal text-white"
            : "bg-theme-surface text-theme-text hover:bg-signal hover:text-white"
        }`}
      >
        <FontAwesomeIcon icon={faShareNodes} className="text-base" />
      </button>

      {/* Opens to the right: the action bar sits at the column's left edge,
          so a right-anchored menu would leave the screen on phones. */}
      {isOpen ? (
        <div
          className={`absolute left-0 top-[calc(100%+0.375rem)] z-40 w-56 overflow-hidden rounded-[3px] border border-theme-border bg-theme-card shadow-float ${menuClassName}`}
        >
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            onClick={() => setIsOpen(false)}
            className="flex h-11 items-center gap-3 px-4 text-sm font-medium text-theme-text transition-colors hover:bg-theme-surface"
          >
            <FontAwesomeIcon icon={faFacebook} className="text-base text-[#1877F2]" />
            <span>Share on Facebook</span>
          </a>
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on X"
            onClick={() => setIsOpen(false)}
            className="flex h-11 items-center gap-3 border-t border-theme-border px-4 text-sm font-medium text-theme-text transition-colors hover:bg-theme-surface"
          >
            <FontAwesomeIcon icon={faXTwitter} className="text-base" />
            <span>Share on X</span>
          </a>
        </div>
      ) : null}
    </div>
  );
};

export default ShareDropdown;
